import React from 'react';

// Helper: Obtener número de semana del año (ISO-8601)
export const getWeekNumber = (d: Date): number => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

// Helper: Obtener los 7 días de una semana específica
export const getDaysOfWeek = (weekNum: number, year: number): Date[] => {
  const firstDayOfYear = new Date(year, 0, 1);
  const daysOffset = (weekNum - 1) * 7 - firstDayOfYear.getDay();
  const sunday = new Date(year, 0, 1 + daysOffset);
  
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(sunday);
    d.setDate(sunday.getDate() + i);
    days.push(d);
  }
  return days;
};

// Helper: Formatear Date a YYYY-MM-DD
export const formatDateStr = (date: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// Helper: Calcular duración de corte en horas decimales
export const getDurationInHours = (start: string, end: string): number => {
  try {
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const startMin = sh * 60 + sm;
    const endMin = eh * 60 + em;
    if (isNaN(startMin) || isNaN(endMin) || endMin <= startMin) return 0;
    return (endMin - startMin) / 60;
  } catch {
    return 0;
  }
};

// Iconos vectoriales SVG limpios para los servicios a cortar
export const ServiceIcon: React.FC<{ type: string; className?: string }> = ({ type, className = "h-5 w-5" }) => {
  if (type === 'electricity') {
    return (
      <svg className={`${className} text-amber-500`} fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
      </svg>
    );
  }
  if (type === 'water') {
    return (
      <svg className={`${className} text-blue-500`} fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.105-7.5 12-7.5 12s-7.5-4.895-7.5-12a7.5 7.5 0 1115 0z" />
      </svg>
    );
  }
  if (type === 'steam') {
    return (
      <svg className={`${className} text-zinc-500`} fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.283 8.283 0 013.137-3.247 8.287 8.287 0 003.225-1.139z" />
      </svg>
    );
  }
  return (
    <svg className={`${className} text-indigo-400`} fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
    </svg>
  );
};

// Iconos SVG limpios para los eventos de planta
export const EventIcon: React.FC<{ name: string; className?: string }> = ({ name, className = "h-3.5 w-3.5" }) => {
  if (name === 'CIP') {
    return (
      <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 21l-.813-5.096L3 15l5.096-.813L9 9l.813 5.096L15 15l-5.187.904zM18 5.25L17.25 9 16.5 5.25 12.75 4.5 16.5 3.75 17.25 0l.75 3.75 3.75.75L18 5.25z" />
      </svg>
    );
  }
  if (name === 'Mantenimiento') {
    return (
      <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.65 2.65 0 1021 17.25l-5.83-5.83m0 0a2.65 2.65 0 11-3.75-3.75M15.17 11.42L3 23m0 0l6.33-6.33M3 23V16.67M21 3a2 2 0 11-2.83 2.83L15 2.67m6 6l-3.17-3.17" />
      </svg>
    );
  }
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  );
};

// Componente Tag Chip Minimalista para eventos
interface EventChipProps {
  name: string;
  isActive: boolean;
  onClick: () => void;
  disabled?: boolean;
  type: 'planned' | 'real';
}
export const EventChip: React.FC<EventChipProps> = ({ name, isActive, onClick, disabled, type }) => {
  const label = name === 'CIP' ? 'CIP' : name === 'Mantenimiento' ? 'Mantenimiento' : 'Formato';
  
  let colorClass = 'bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100 hover:text-slate-600';
  if (isActive) {
    colorClass = type === 'planned'
      ? 'bg-amber-100/60 border-amber-300/80 text-[#d1a228]'
      : 'bg-emerald-100/60 border-emerald-300 text-emerald-800';
  }

  if (disabled) {
    return (
      <div className={`flex items-center gap-1.5 px-3 py-1 rounded-xl border text-[11px] font-bold transition-all opacity-70 ${colorClass}`}>
        <EventIcon name={name} />
        <span>{label}</span>
      </div>
    );
  }

  return (
    <button 
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1 rounded-xl border text-[11px] font-black transition-all ${colorClass}`}
    >
      <EventIcon name={name} />
      <span>{label}</span>
    </button>
  );
};

// Iconos vectoriales SVG para celdas del calendario principal
export const PendingAlertSVG: React.FC<{ className?: string }> = ({ className = "h-4.5 w-4.5" }) => (
  <svg className={`${className} text-red-600 animate-pulse flex-shrink-0`} fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);

export const SuccessCheckSVG: React.FC<{ className?: string }> = ({ className = "h-4.5 w-4.5" }) => (
  <svg className={`${className} text-emerald-600 flex-shrink-0`} fill="none" viewBox="0 0 24 24" strokeWidth="3" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);
